import React, { useState, useEffect } from 'react';
import { API_URL } from '../../lib/api';
import { parseServicePackageFeatures } from '../../lib/formatters';

function RetailCompanyModal({ company, isOpen, onClose, onSave, isNewCompany = false }) {
  const [formData, setFormData] = useState({
    company_name: '',
    legal_name: '',
    business_number: '',
    gst_hst_number: '',
    pst_number: '',
    incorporation_number: '',
    street_address: '',
    city: '',
    province: 'British Columbia',
    postal_code: '',
    country: 'Canada',
    phone_number: '',
    email: '',
    website: '',
    business_type: 'corporation',
    industry_sector: '',
    contract_signed: false,
    contract_uploaded: false,
    contract_file_path: '',
    contract_signed_date: '',
    optiprime_core: false,
    optilaw_legal: false,
    white_label_enabled: false,
    white_label_portal_url: '',
    custom_backend_enabled: false,
    custom_backend_portal_url: '',
    // Package Selection
    package_tier: '',
    // Master Admin fields
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_username: '',
    admin_phone: '',
    admin_title: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contractFile, setContractFile] = useState(null);
  const [plainTextPassword, setPlainTextPassword] = useState(''); // Store generated password
  const [showPassword, setShowPassword] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedPackageDetails, setSelectedPackageDetails] = useState(null);

  // Fetch available packages
  const fetchPackages = async () => {
    setPackagesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/service-packages`);
      if (response.ok) {
        const packages = await response.json();
        setAvailablePackages(packages);
        
        // Set selected package details if package_tier is already set
        if (formData.package_tier) {
          const selectedPkg = packages.find(pkg => pkg.package_tier === formData.package_tier);
          setSelectedPackageDetails(selectedPkg);
        }
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setPackagesLoading(false);
    }
  };


  // Generate Master Admin password with MA+ prefix pattern
  const generateMasterAdminPassword = (firstName = '', lastName = '', telNo = '') => {
    if (!firstName && !lastName) return '';
    
    // Create MA+ interleaved pattern: MA + [FirstInitial][LastInitial][FirstPhone] + [InterleavePattern] + [RandomNumbers]
    let result = 'MA';
    if (firstName.length > 0) result += firstName[0].toUpperCase();
    if (lastName.length > 0) result += lastName[0].toUpperCase();
    if (telNo.length > 0) result += telNo[0]; // Add first phone digit after initials
    
    // Add interleaved pattern starting from index 1
    let i = 1;
    while (i < Math.max(firstName.length, lastName.length, telNo.length)) {
      if (i < firstName.length) result += firstName[i].toLowerCase();
      if (i < lastName.length) result += lastName[i].toLowerCase();
      if (i < telNo.length) result += telNo[i];
      i++;
    }
    
    // Add random numbers at the end (2-3 digits)
    const randomNumbers = Math.floor(Math.random() * 900) + 100; // 3-digit number
    result += randomNumbers;
    
    return result;
  };

  const handleMasterAdminPasswordReset = async (userId, email) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/master-admin/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`Password reset successfully!\n\nNew temporary password: ${result.temp_password}\n\nA welcome email will be sent to ${email}`);
      } else {
        alert(result.message || 'Failed to reset password.');
      }
    } catch (error) {
      console.error('Error resetting Master Admin password:', error);
      alert('Error resetting password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Fetch packages when modal opens
      fetchPackages();
      
    if (company && !isNewCompany) {
      setFormData({
        ...company,
          package_tier: company.package_tier || '',
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_username: '',
        admin_phone: '',
        admin_title: ''
      });
    } else {
      setFormData({
        company_name: '',
        legal_name: '',
        business_number: '',
        gst_hst_number: '',
        pst_number: '',
        incorporation_number: '',
        street_address: '',
        city: '',
        province: 'British Columbia',
        postal_code: '',
        country: 'Canada',
        phone_number: '',
        email: '',
        website: '',
        business_type: 'corporation',
        industry_sector: '',
        contract_signed: false,
        contract_uploaded: false,
        contract_file_path: '',
        contract_signed_date: '',
        optiprime_core: false,
        optilaw_legal: false,
        white_label_enabled: false,
        white_label_portal_url: '',
        custom_backend_enabled: false,
        custom_backend_portal_url: '',
        package_tier: '',
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_username: '',
        admin_phone: '',
        admin_title: ''
      });
    }
    setError('');
    }
  }, [company, isNewCompany, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const newFormData = {
      ...prev,
        [name]: newValue
      };
      
      // Auto-generate password when Master Admin details are entered (only for new companies)
      if (isNewCompany && (name === 'admin_first_name' || name === 'admin_last_name' || name === 'admin_phone')) {
        const autoPassword = generateMasterAdminPassword(
          name === 'admin_first_name' ? value : newFormData.admin_first_name,
          name === 'admin_last_name' ? value : newFormData.admin_last_name,
          name === 'admin_phone' ? value : newFormData.admin_phone
        );
        if (autoPassword && autoPassword !== 'MA') {
          setPlainTextPassword(autoPassword);
        }
      }

      // Update selected package details when package tier changes
      if (name === 'package_tier') {
        const selectedPkg = availablePackages.find(pkg => pkg.package_tier === value);
        setSelectedPackageDetails(selectedPkg);
      }
      
      return newFormData;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setContractFile(file);
    if (file) {
      setFormData(prev => ({
        ...prev,
        contract_uploaded: true
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate minimal required fields for company creation
      const requiredFields = ['company_name', 'legal_name', 'business_type'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Please fill in required company fields: ${missingFields.join(', ')}`);
      }

      // Validate that at least one OptiPrime product is selected
      if (!formData.optiprime_core && !formData.optilaw_legal) {
        throw new Error('Please select at least one OptiPrime product (OptiPrime Core or OptiLaw Legal)');
      }

      // Validate Master Admin fields if creating new company
      if (isNewCompany) {
        const adminFields = ['admin_first_name', 'admin_last_name', 'admin_email', 'admin_username'];
        const missingAdminFields = adminFields.filter(field => !formData[field]);
        
        if (missingAdminFields.length > 0) {
          throw new Error(`Please fill in all Master Admin fields: ${missingAdminFields.join(', ')}`);
        }
      }

      // Prepare data for JSON submission
      const submitData = { ...formData };

      // Add generated password for Master Admin if available
      if (isNewCompany && plainTextPassword) {
        submitData.admin_password = plainTextPassword;
      }

      // Note: Contract file upload will be handled separately if needed
      if (contractFile) {
        console.warn('Contract file upload not yet implemented for JSON submission');
      }

      const url = isNewCompany ? '/api/retail-companies' : `/api/retail-companies/${company.id}`;
      const method = isNewCompany ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save company');
      }

      const savedCompany = await response.json();
      
      // If editing existing company and Master Admin fields are provided, update Master Admin
      if (!isNewCompany && company && company.master_admin_user_id && 
          (formData.admin_first_name || formData.admin_last_name || formData.admin_email || formData.admin_username)) {
        
        try {
          const masterAdminResponse = await fetch(`/api/master-admin/${company.master_admin_user_id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              firstName: formData.admin_first_name || company.admin_first_name,
              lastName: formData.admin_last_name || company.admin_last_name,
              email: formData.admin_email || company.admin_email,
              username: formData.admin_username || company.admin_username,
              department: 'Technology',
              role: 'Master Admin',
              company_id: company.id
            })
          });
          
          const masterAdminResult = await masterAdminResponse.json();
          
          if (masterAdminResult.success) {
            alert('Company and Master Admin updated successfully!');
          } else {
            alert('Company updated, but Master Admin update failed: ' + masterAdminResult.message);
          }
        } catch (masterAdminError) {
          console.error('Error updating Master Admin:', masterAdminError);
          alert('Company updated successfully, but Master Admin update failed.');
        }
      }
      
      onSave(savedCompany);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content retail-company-modal">
        <div className="modal-header">
          <h2>{isNewCompany ? 'Add New Enterprise' : 'Edit Enterprise'}</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <div className="form-sections">
            {/* Company Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-building"></i> Company Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="company_name">Company Name *</label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_name">Legal Name *</label>
                  <input
                    type="text"
                    id="legal_name"
                    name="legal_name"
                    value={formData.legal_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="business_type">Business Type *</label>
                  <select
                    id="business_type"
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="partnership">Partnership</option>
                    <option value="corporation">Corporation</option>
                    <option value="cooperative">Cooperative</option>
                    <option value="non_profit">Non-Profit</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="industry_sector">Industry Sector</label>
                  <input
                    type="text"
                    id="industry_sector"
                    name="industry_sector"
                    value={formData.industry_sector}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Package Selection Section */}
            <div className="form-section">
              <h3><i className="fas fa-box"></i> Service Package</h3>
              
              {packagesLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <i className="fas fa-spinner fa-spin"></i> Loading packages...
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="package_tier">Select Package *</label>
                    <select
                      id="package_tier"
                      name="package_tier"
                      value={formData.package_tier}
                      onChange={handleInputChange}
                      required
                      style={{
                        padding: '12px',
                        fontSize: '14px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--surface-color)',
                        color: 'var(--text-primary)',
                        width: '100%'
                      }}
                    >
                      <option value="">-- Select Package --</option>
                      {availablePackages.map((pkg) => (
                        <option key={pkg.id} value={pkg.package_tier}>
                          {pkg.display_name} - ${pkg.price_monthly}/month
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedPackageDetails && (
                    <div className="package-details-card" style={{
                      marginTop: '16px',
                      padding: '20px',
                      backgroundColor: 'rgba(60, 252, 249, 0.05)',
                      border: '1px solid rgba(60, 252, 249, 0.2)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', color: 'var(--text-primary)', fontSize: '18px' }}>
                            {selectedPackageDetails.display_name}
                          </h4>
                          <div style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            backgroundColor: 'var(--primary-color)',
                            color: 'var(--text-on-primary)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {selectedPackageDetails.package_tier}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>
                            ${selectedPackageDetails.price_monthly}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            per month
                          </div>
                          <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                            ${selectedPackageDetails.price_yearly}/year
                          </div>
                        </div>
                      </div>
                      
                      <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                        {selectedPackageDetails.description}
                      </p>
                      
                      {parseServicePackageFeatures(selectedPackageDetails.features).length > 0 && (
                        <div>
                          <h5 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)', fontSize: '14px' }}>
                            Features Included:
                          </h5>
                          <ul style={{ 
                            margin: '0', 
                            paddingLeft: '0', 
                            listStyle: 'none',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '4px'
                          }}>
                            {parseServicePackageFeatures(selectedPackageDetails.features).map((feature, index) => (
                              <li key={index} style={{
                                padding: '4px 0',
                                color: 'var(--text-secondary)',
                                fontSize: '13px',
                                position: 'relative',
                                paddingLeft: '16px'
                              }}>
                                <span style={{
                                  position: 'absolute',
                                  left: '0',
                                  color: 'var(--success-color)',
                                  fontWeight: 'bold'
                                }}>✓</span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* BC Legal Requirements Section */}
            <div className="form-section">
              <h3><i className="fas fa-gavel"></i> BC Legal Requirements</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="business_number">BC Business Number</label>
                  <input
                    type="text"
                    id="business_number"
                    name="business_number"
                    value={formData.business_number}
                    onChange={handleInputChange}
                    placeholder="e.g., BC1234567"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="gst_hst_number">GST/HST Registration Number</label>
                  <input
                    type="text"
                    id="gst_hst_number"
                    name="gst_hst_number"
                    value={formData.gst_hst_number}
                    onChange={handleInputChange}
                    placeholder="e.g., 123456789RT0001"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pst_number">BC PST Number</label>
                  <input
                    type="text"
                    id="pst_number"
                    name="pst_number"
                    value={formData.pst_number}
                    onChange={handleInputChange}
                    placeholder="e.g., PST-1234-5678"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="incorporation_number">BC Incorporation Number</label>
                  <input
                    type="text"
                    id="incorporation_number"
                    name="incorporation_number"
                    value={formData.incorporation_number}
                    onChange={handleInputChange}
                    placeholder="e.g., BC1234567"
                  />
                </div>
              </div>
            </div>

            {/* Business Address Section */}
            <div className="form-section">
              <h3><i className="fas fa-map-marker-alt"></i> Business Address</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="street_address">Street Address</label>
                  <input
                    type="text"
                    id="street_address"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="province">Province</label>
                  <select
                    id="province"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                  >
                    <option value="British Columbia">British Columbia</option>
                    <option value="Alberta">Alberta</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="Ontario">Ontario</option>
                    <option value="Quebec">Quebec</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="Prince Edward Island">Prince Edward Island</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    <option value="Northwest Territories">Northwest Territories</option>
                    <option value="Nunavut">Nunavut</option>
                    <option value="Yukon">Yukon</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="postal_code">Postal Code</label>
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    placeholder="e.g., V6B 1A1"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-phone"></i> Contact Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="phone_number">Phone Number</label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="e.g., (604) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>
            </div>

            {/* Contract Information Section */}
            <div className="form-section">
              <h3><i className="fas fa-file-contract"></i> Contract Information</h3>
              <div className="form-grid">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="contract_signed"
                      checked={formData.contract_signed}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    Contract Signed
                  </label>
                </div>
                <div className="form-group">
                  <label htmlFor="contract_signed_date">Contract Signed Date</label>
                  <input
                    type="date"
                    id="contract_signed_date"
                    name="contract_signed_date"
                    value={formData.contract_signed_date}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="contract_file">Upload Contract Document</label>
                  <input
                    type="file"
                    id="contract_file"
                    name="contract_file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                  />
                  <small>Accepted formats: PDF, DOC, DOCX</small>
                </div>
              </div>
            </div>

            {/* OptiPrime Products Section */}
            <div className="form-section">
              <h3><i className="fas fa-cube"></i> OptiPrime Products</h3>
              <div className="checkbox-grid">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="optiprime_core"
                      checked={formData.optiprime_core}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    OptiPrime Core
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="optilaw_legal"
                      checked={formData.optilaw_legal}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    OptiLaw Legal
                  </label>
                </div>
              </div>
            </div>

            {/* Enterprise Deployment Options */}
            <div className="form-section">
              <h3><i className="fas fa-layer-group"></i> Enterprise Deployment Options</h3>
              <p className="section-description">
                Configure whether this enterprise client needs a white-label experience and/or a dedicated backend management portal.
                Example: <strong>Optimum Realty Inc.</strong> uses both — branded login at <code>app.optiprime.ai</code> and a custom admin portal at <code>optimumadmin.optiprime.ai</code>.
              </p>
              <div className="checkbox-grid">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="white_label_enabled"
                      checked={formData.white_label_enabled}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    White-label deployment
                  </label>
                  <small>Custom branding, dedicated subdomain, and client-facing portal experience.</small>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="custom_backend_enabled"
                      checked={formData.custom_backend_enabled}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    Custom backend management portal
                  </label>
                  <small>Full dedicated admin dashboard for brokerage/enterprise operations beyond standard OptiPrime core.</small>
                </div>
              </div>
              {formData.white_label_enabled && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="white_label_portal_url">White-label portal URL</label>
                  <input
                    type="url"
                    id="white_label_portal_url"
                    name="white_label_portal_url"
                    value={formData.white_label_portal_url}
                    onChange={handleInputChange}
                    placeholder="https://optimum.optiprime.ai"
                  />
                </div>
              )}
              {formData.custom_backend_enabled && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="custom_backend_portal_url">Custom backend admin URL</label>
                  <input
                    type="url"
                    id="custom_backend_portal_url"
                    name="custom_backend_portal_url"
                    value={formData.custom_backend_portal_url}
                    onChange={handleInputChange}
                    placeholder="https://optimumadmin.optiprime.ai"
                  />
                </div>
              )}
            </div>

            <div className="form-section">
              <h3><i className="fas fa-building"></i> Properties</h3>
              <p className="section-description">
                After this enterprise is saved, add houses, apartment buildings, offices, and other real estate from the enterprise card. The add-property form changes with the property type you select.
              </p>
            </div>

            {/* Master Admin Section - Only for new companies */}
            {isNewCompany && (
              <div className="form-section">
                <h3><i className="fas fa-user-shield"></i> Master Admin User</h3>
                <p className="section-description">
                  Create the Master Admin user account for this company. A welcome email will be sent to this user.
                </p>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="admin_first_name">First Name *</label>
                    <input
                      type="text"
                      id="admin_first_name"
                      name="admin_first_name"
                      value={formData.admin_first_name}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_last_name">Last Name *</label>
                    <input
                      type="text"
                      id="admin_last_name"
                      name="admin_last_name"
                      value={formData.admin_last_name}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_email">Email Address *</label>
                    <input
                      type="email"
                      id="admin_email"
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_username">Username *</label>
                    <input
                      type="text"
                      id="admin_username"
                      name="admin_username"
                      value={formData.admin_username}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_phone">Phone Number</label>
                    <input
                      type="tel"
                      id="admin_phone"
                      name="admin_phone"
                      value={formData.admin_phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_title">Job Title</label>
                    <input
                      type="text"
                      id="admin_title"
                      name="admin_title"
                      value={formData.admin_title}
                      onChange={handleInputChange}
                      placeholder="e.g., CEO, Manager, etc."
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_department">Department *</label>
                    <select
                      id="admin_department"
                      name="admin_department"
                      value={formData.admin_department || 'Technology'}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    >
                      <option value="Technology">Technology</option>
                      <option value="Property Management">Property Management</option>
                      <option value="Accounting">Accounting</option>
                      <option value="Sales">Sales</option>
                      <option value="Legal">Legal</option>
                      <option value="Administration">Administration</option>
                      <option value="Executive">Executive</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin_role">Role *</label>
                    <select
                      id="admin_role"
                      name="admin_role"
                      value={formData.admin_role || 'Master Admin'}
                      onChange={handleInputChange}
                      required={isNewCompany}
                    >
                      <option value="Master Admin">Master Admin</option>
                      <option value="Admin">Admin</option>
                      <option value="CEO">CEO</option>
                      <option value="Senior Management">Senior Management</option>
                      <option value="Director">Director</option>
                    </select>
                  </div>
                </div>

                {/* Password Management Section */}
                <div className="password-group">
                  <h4><i className="fas fa-key"></i> Password Management</h4>
                  {plainTextPassword ? (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                        <strong>Auto-generated Password Ready</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#155724' }}>
                        Password generated using MA+ prefix pattern: <strong>MA</strong> + Name + Surname + Phone (interweaved)
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#856404' }}>
                        <i className="fas fa-info-circle"></i> Password will be auto-generated when you enter the Master Admin's first name, last name, and phone number.
                      </div>
                    </div>
                  )}
                  
                  {plainTextPassword && (
                    <div className="password-input-group">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={plainTextPassword}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #28a745',
                          borderRadius: '4px',
                          backgroundColor: '#f8fff9',
                          fontFamily: 'monospace',
                          fontSize: '14px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #28a745',
                          borderRadius: '4px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(plainTextPassword);
                          // You could add a toast notification here
                        }}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Master Admin Management - For existing companies */}
            {!isNewCompany && company && company.master_admin_created && (
              <div className="form-section">
                <h3><i className="fas fa-user-shield"></i> Master Admin Management</h3>
                <p className="section-description">
                  Manage the Master Admin account for this company. You can update their details and reset their password.
                </p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="edit_admin_first_name">First Name *</label>
                    <input
                      type="text"
                      id="edit_admin_first_name"
                      name="admin_first_name"
                      value={formData.admin_first_name || company.admin_first_name || ''}
                      onChange={handleInputChange}
                      placeholder="Master Admin First Name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_admin_last_name">Last Name *</label>
                    <input
                      type="text"
                      id="edit_admin_last_name"
                      name="admin_last_name"
                      value={formData.admin_last_name || company.admin_last_name || ''}
                      onChange={handleInputChange}
                      placeholder="Master Admin Last Name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_admin_email">Email Address *</label>
                    <input
                      type="email"
                      id="edit_admin_email"
                      name="admin_email"
                      value={formData.admin_email || company.admin_email || ''}
                      onChange={handleInputChange}
                      placeholder="Master Admin Email"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_admin_username">Username *</label>
                    <input
                      type="text"
                      id="edit_admin_username"
                      name="admin_username"
                      value={formData.admin_username || company.admin_username || ''}
                      onChange={handleInputChange}
                      placeholder="Master Admin Username"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_admin_department">Department</label>
                    <select
                      id="edit_admin_department"
                      name="admin_department"
                      value="Technology"
                      disabled
                    >
                      <option value="Technology">Technology</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_admin_role">Role</label>
                    <select
                      id="edit_admin_role"
                      name="admin_role"
                      value="Master Admin"
                      disabled
                    >
                      <option value="Master Admin">Master Admin</option>
                    </select>
                  </div>
                </div>
                
                <div className="password-section">
                  <h4><i className="fas fa-key"></i> Password Management</h4>
                  <p>Reset the Master Admin's password if they've forgotten it or need a new one.</p>
                  <button 
                    type="button"
                    className="btn btn-warning"
                    onClick={() => {
                      if (window.confirm(`Reset password for ${formData.admin_first_name || company.admin_first_name} ${formData.admin_last_name || company.admin_last_name}?\n\nA new temporary password will be generated and emailed to them.`)) {
                        handleMasterAdminPasswordReset(company.master_admin_user_id, formData.admin_email || company.admin_email);
                      }
                    }}
                    title="Reset Master Admin Password"
                  >
                    <i className="fas fa-sync-alt"></i> Reset Password & Send Email
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {isNewCompany ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {isNewCompany ? 'Create Company' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RetailCompanyModal;
